import {
    useDeleteAgentExecution,
    useGetAgentExecution,
    useListAgentExecutions,
    type PlainAgentExecution,
} from "@/queries/agent-queries";
import {
    useDeleteEnvironment,
    useEnvironment,
    useListEnvironments,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { agentExecutionName } from "@/utils/agent-executions";
import { useMemo, useState } from "react";
import { useListProjects } from "@/queries/project-queries";
import { getRepoUrl } from "@/repo-url";
import { useLocalStorage } from "@/hooks/use-local-storage";
import {
    keyAgentExecutionSessionMapping,
    usedEnvironmentIds,
    type Session,
} from "@/components/ai/session/session-model";
import { useDeleteSessionPlan, useSessionPlan } from "@/hooks/use-session-plan";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";

export function useSessionName(session?: Session): string {
    const { data: projects } = useListProjects();
    const { data: environment } = useEnvironment(session?.environmentIds?.[0]);
    const { initialAgentExecution } = useSessionAgentExecution(session);
    const localSessionName = useLocalSessionName(session);

    if (localSessionName) {
        return localSessionName;
    }

    if (initialAgentExecution) {
        return agentExecutionName(initialAgentExecution);
    }

    if (environment) {
        const projectId = environment.metadata?.projectId;
        const repoUrl = getRepoUrl(environment);
        if (projectId) {
            const project = projects?.projects.find((project) => project.id === projectId);
            if (project?.metadata?.name) {
                return project.metadata.name;
            }
        } else if (repoUrl) {
            return `${repoUrl.account}/${repoUrl.repo}`;
        }
    }

    return "no-name";
}

function localSessionNameKey(session?: Session): string {
    return `session-name-${session?.id}`;
}

export function useLocalSessionName(session?: Session): string {
    const { storedValue: localSessionName } = useLocalStorage(localSessionNameKey(session), "");
    return localSessionName;
}

export function useSessionEnvironment(session?: Session): PlainEnvironment | undefined {
    const { data: environment } = useEnvironment(session?.environmentIds?.[0]);
    return environment;
}

export function useSessionAgentExecution(session?: Session): {
    initialAgentExecution: PlainAgentExecution | undefined;
    activeAgentExecution: PlainAgentExecution | undefined;
} {
    const initialAgentExecutionId = session?.agentExecutionIds?.[0];
    const activeAgentExecutionId = session?.agentExecutionIds?.[session.agentExecutionIds.length - 1];
    const { data: initialAgentExecution } = useGetAgentExecution(initialAgentExecutionId);
    const { data: activeAgentExecution } = useGetAgentExecution(activeAgentExecutionId);
    return { initialAgentExecution, activeAgentExecution };
}

/**
 * Returns a list of sessions that can be used to render the dashboard.
 * A session is a collection of agent executions and environments that are related to each other.
 *
 * @returns a list of sessions
 */
export function useSessions() {
    const { data: environmentsData, isPending: isPendingWs } = useListEnvironments();
    const { data: agentExecutions, isPending: isPendingAgentExecutions } = useListAgentExecutions();

    const sortedAgentExecutionsAsc = useMemo(() => {
        return (agentExecutions?.agentExecutions ?? []).sort((a, b) => {
            if (a.metadata?.createdAt && b.metadata?.createdAt) {
                return Number(a.metadata.createdAt.seconds) - Number(b.metadata.createdAt.seconds);
            }
            return 0;
        });
    }, [agentExecutions]);

    const sessions = useMemo(
        () => computeSessions(sortedAgentExecutionsAsc, environmentsData?.environments ?? []),
        [sortedAgentExecutionsAsc, environmentsData],
    );

    return {
        isPending: isPendingWs || isPendingAgentExecutions,
        sessions,
    };
}

export function useDeleteSession(session: Session) {
    const deleteAgentExecution = useDeleteAgentExecution();
    const deleteEnvironment = useDeleteEnvironment();
    const renameSession = useRenameSession(session);
    const deletePlan = useDeleteSessionPlan(session.id);
    const plan = useSessionPlan(session.id);

    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const deleteSession = async ({ deleteEnvironments }: { deleteEnvironments?: string[] }) => {
        const promises: Promise<unknown>[] = session.agentExecutionIds.map((agentExecutionId) => {
            window.localStorage.removeItem(keyAgentExecutionSessionMapping(agentExecutionId));
            return deleteAgentExecution.mutateAsync({ agentExecutionId });
        });
        promises.push(renameSession.mutateAsync(undefined)); // clear the name in local storage
        for (const environmentId of deleteEnvironments ?? []) {
            const deleteEnvironmentPromise = deleteEnvironment.mutateAsync({ environmentId, force: true });
            promises.push(deleteEnvironmentPromise);
        }

        if (plan) {
            const deletePlanPromise = deletePlan.mutateAsync(plan);
            promises.push(deletePlanPromise);
        }

        try {
            setIsPending(true);
            await Promise.all(promises);
            setIsPending(false);
        } catch (error) {
            setIsPending(false);
            if (error instanceof Error) {
                setError(error);
            }
        }
    };

    return {
        isPending,
        error,
        deleteAsync: deleteSession,
    };
}

export function computeSessions(agentExecutions: PlainAgentExecution[], environments: PlainEnvironment[]): Session[] {
    const filteredEnvironments = environments.filter(
        (env) => env.spec?.desiredPhase !== EnvironmentPhase.DELETED && env.status?.phase !== EnvironmentPhase.DELETING,
    );

    agentExecutions = agentExecutions.sort((a, b) => {
        if (a.metadata?.createdAt && b.metadata?.createdAt) {
            return Number(a.metadata.createdAt.seconds) - Number(b.metadata.createdAt.seconds);
        }
        return 0;
    });

    const agentExecutionsById = new Map<string, PlainAgentExecution>();
    for (const execution of agentExecutions) {
        agentExecutionsById.set(execution.id, execution);
    }

    const environmentsUsedByAgentExecutions = new Set();
    for (const execution of agentExecutions) {
        for (const environmentId of usedEnvironmentIds(execution)) {
            environmentsUsedByAgentExecutions.add(environmentId);
        }
    }

    const agentExecutionSessionMap = new Map<string, string>();
    for (const execution of agentExecutions) {
        const sessionId = window.localStorage.getItem(keyAgentExecutionSessionMapping(execution.id));
        if (sessionId) {
            agentExecutionSessionMap.set(execution.id, sessionId);
        }
    }

    const standAloneAgentExecutions = new Set(
        agentExecutions
            .filter((execution) => execution.spec?.codeContext?.context?.case !== "environmentId")
            .filter((execution) => !execution.status?.usedEnvironments.length)
            .map((execution) => execution.id) ?? [],
    );

    const standAloneEnvironments = new Set(
        filteredEnvironments
            .filter((environment) => !environmentsUsedByAgentExecutions.has(environment.id))
            .map((environment) => environment.id) ?? [],
    );

    const sessions: Session[] = [];
    for (const agentExecution of agentExecutions) {
        if (standAloneAgentExecutions.has(agentExecution.id)) {
            const sessionId = agentExecutionSessionMap.get(agentExecution.id) ?? agentExecution.id;
            const existingSession = sessions.find((session) => session.id === sessionId);
            if (existingSession) {
                existingSession.agentExecutionIds.push(agentExecution.id);
            } else {
                sessions.push({ id: sessionId, environmentIds: [], agentExecutionIds: [agentExecution.id] });
            }
        } else {
            const environmentIds = usedEnvironmentIds(agentExecution);
            const environmentId = environmentIds[0];
            const existingSessionId =
                agentExecutionSessionMap.get(agentExecution.id) ??
                sessions.find((session) => session.environmentIds.includes(environmentId))?.id;
            const existingSession = existingSessionId
                ? sessions.find((session) => session.id === existingSessionId)
                : undefined;

            if (existingSession) {
                existingSession.agentExecutionIds.push(agentExecution.id);
            } else {
                sessions.push({
                    id: agentExecution.id,
                    environmentIds: environmentIds,
                    agentExecutionIds: [agentExecution.id],
                });
            }
        }
    }

    for (const environmentId of standAloneEnvironments) {
        sessions.push({ id: environmentId, environmentIds: [environmentId], agentExecutionIds: [] });
    }

    // Sort sessions descending by the creationTime of the initial agent execution - newest sessions first.
    return sessions.sort((a, b) => {
        const aInitialAgentExecution = agentExecutionsById.get(a.agentExecutionIds[0]);
        const bInitialAgentExecution = agentExecutionsById.get(b.agentExecutionIds[0]);
        const aCreationTime = Number(aInitialAgentExecution?.metadata?.createdAt?.seconds ?? 0);
        const bCreationTime = Number(bInitialAgentExecution?.metadata?.createdAt?.seconds ?? 0);
        return bCreationTime - aCreationTime;
    });
}

export function useRenameSession(session: Session) {
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { setValue: setLocalSessionName } = useLocalStorage(localSessionNameKey(session), "");

    const renameSession = async (name?: string) => {
        try {
            setIsPending(true);
            setLocalSessionName(name);
            setIsPending(false);
        } catch (error) {
            setIsPending(false);
            if (error instanceof Error) {
                setError(error);
            }
        }
        return Promise.resolve();
    };

    return {
        isPending,
        error,
        mutateAsync: renameSession,
    };
}
